import { useAuthContext } from '@/auth/useAuthContext';
import { CustomerCombobox } from '@/components/CustomerCombobox';
import { Modal } from '@/components/Modal';
import { PetCombobox } from '@/components/PetCombobox';
import { RunPicker } from '@/components/RunPicker';
import { StatusPill } from '@/components/StatusPill';
import {
  ApiError,
  createWaitlistEntry,
  deleteWaitlistEntry,
  listWaitlist,
  promoteWaitlistEntry,
  updateWaitlistEntry,
} from '@/lib/api';
import type { Customer, PetWithVaccinations, WaitlistEntry, WaitlistStatus } from '@/lib/types';
import { useRealtime } from '@/lib/useRealtime';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import { cn } from '@/lib/utils';
import { Cat, Dog, Plus, Trash2, TrendingUp, UserCheck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const STATUS_OPTIONS: WaitlistStatus[] = ['waiting', 'contacted', 'booked', 'expired', 'cancelled'];

// ---------------------------------------------------------------------------
// Add modal
// ---------------------------------------------------------------------------

interface AddModalProps {
  facilityId: string;
  onClose: () => void;
  onAdded: () => void;
}

function AddModal({ facilityId, onClose, onAdded }: AddModalProps) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [pet, setPet] = useState<PetWithVaccinations | null>(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkout, setCheckout] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    if (!customer || !pet || !checkIn || !checkout) return;
    setBusy(true);
    setError(null);
    try {
      await createWaitlistEntry({
        facilityId,
        customerId: customer.id,
        petId: pet.id,
        requestedCheckIn: new Date(checkIn).toISOString(),
        requestedCheckout: new Date(checkout).toISOString(),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      onAdded();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label="Add to waitlist">
      <form
        className="p-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <h2 className="font-heading text-lg font-medium">Add to waitlist</h2>

        <div>
          <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Customer
          </p>
          <CustomerCombobox value={customer} onChange={setCustomer} />
        </div>

        <div>
          <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Pet
          </p>
          <PetCombobox customerId={customer?.id ?? null} value={pet} onChange={setPet} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="waitlist-checkin"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Requested check-in
            </label>
            <input
              id="waitlist-checkin"
              type="date"
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label
              htmlFor="waitlist-checkout"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Requested checkout
            </label>
            <input
              id="waitlist-checkout"
              type="date"
              value={checkout}
              onChange={(e) => setCheckout(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="waitlist-notes"
            className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            Notes (optional)
          </label>
          <textarea
            id="waitlist-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Any special requests or requirements…"
            className="w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!customer || !pet || !checkIn || !checkout || busy}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Adding…' : 'Add to waitlist'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Promote modal
// ---------------------------------------------------------------------------

interface PromoteModalProps {
  entry: WaitlistEntry;
  facilityId: string;
  onClose: () => void;
  onPromoted: () => void;
}

function PromoteModal({ entry, facilityId, onClose, onPromoted }: PromoteModalProps) {
  const noun = useSpaceNoun();
  const [checkIn, setCheckIn] = useState(entry.requestedCheckIn.slice(0, 10));
  const [checkout, setCheckout] = useState(entry.requestedCheckout.slice(0, 10));
  const [runId, setRunId] = useState('');
  const [feeding, setFeeding] = useState('');
  const [medication, setMedication] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  async function handlePromote() {
    if (!checkIn || !checkout) return;
    setBusy(true);
    setError(null);
    try {
      await promoteWaitlistEntry(entry.id, {
        checkIn: new Date(checkIn).toISOString(),
        checkout: new Date(checkout).toISOString(),
        runId: runId.trim() || null,
        feeding: feeding.trim() || null,
        medication: medication.trim() || null,
      });
      setSuccessMsg('Promoted to booking');
      setTimeout(() => {
        onPromoted();
      }, 800);
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? err.code : 'Something went wrong';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" aria-label="Promote to booking">
      <div className="p-6 space-y-4">
        <h2 className="font-heading text-lg font-medium">Promote to booking</h2>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="promote-checkin"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Check-in
            </label>
            <input
              id="promote-checkin"
              type="date"
              value={checkIn}
              onChange={(e) => setCheckIn(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label
              htmlFor="promote-checkout"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Checkout
            </label>
            <input
              id="promote-checkout"
              type="date"
              value={checkout}
              onChange={(e) => setCheckout(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            {noun.Singular} (optional)
          </p>
          <RunPicker
            facilityId={facilityId}
            value={runId || null}
            onChange={(v) => setRunId(v ?? '')}
            startsAt={checkIn ? new Date(checkIn).toISOString() : undefined}
            endsAt={checkout ? new Date(checkout).toISOString() : undefined}
            species={entry.petSpecies ?? undefined}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="promote-feeding"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Feeding (optional)
            </label>
            <input
              id="promote-feeding"
              type="text"
              value={feeding}
              onChange={(e) => setFeeding(e.target.value)}
              placeholder="e.g. 2x daily"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div>
            <label
              htmlFor="promote-medication"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Medication (optional)
            </label>
            <input
              id="promote-medication"
              type="text"
              value={medication}
              onChange={(e) => setMedication(e.target.value)}
              placeholder="e.g. Apoquel morning"
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>

        {successMsg !== null && <p className="text-sm text-success">{successMsg}</p>}
        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!checkIn || !checkout || busy}
            onClick={() => void handlePromote()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Promoting…' : 'Promote to booking'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Waitlist row
// ---------------------------------------------------------------------------

interface RowProps {
  entry: WaitlistEntry;
  onMarkContacted: () => void;
  onPromote: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

function WaitlistRow({ entry, onMarkContacted, onPromote, onCancel, onDelete }: RowProps) {
  const isActionable = entry.status === 'waiting' || entry.status === 'contacted';

  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-surface px-4 py-3">
      <div className="mt-0.5 shrink-0 text-muted-foreground">
        {entry.petId ? (
          <Dog className="size-4" strokeWidth={1.75} />
        ) : (
          <Cat className="size-4" strokeWidth={1.75} />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-sm">
            {entry.petName || `Pet ${entry.petId.slice(-6)}`}
          </span>
          <span className="text-xs text-muted-foreground">
            {entry.customerName || `Customer ${entry.customerId.slice(-6)}`}
          </span>
          <StatusPill status={entry.status} />
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {formatDate(entry.requestedCheckIn)} – {formatDate(entry.requestedCheckout)}
        </p>
        {entry.notes && (
          <p className="mt-1 text-xs text-muted-foreground line-clamp-1">{entry.notes}</p>
        )}
      </div>

      {isActionable && (
        <div className="flex shrink-0 items-center gap-2">
          {entry.status === 'waiting' && (
            <button
              type="button"
              onClick={onMarkContacted}
              title="Mark contacted"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground press"
            >
              <UserCheck className="size-3.5" strokeWidth={1.75} />
              Contacted
            </button>
          )}
          <button
            type="button"
            onClick={onPromote}
            title="Promote to booking"
            className="flex items-center gap-1 text-xs text-primary hover:underline press"
          >
            <TrendingUp className="size-3.5" strokeWidth={1.75} />
            Promote
          </button>
          <button
            type="button"
            onClick={onCancel}
            title="Cancel waitlist entry"
            className="text-xs text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDelete}
            title="Delete waitlist entry"
            className="text-muted-foreground hover:text-destructive press"
          >
            <Trash2 className="size-3.5" strokeWidth={1.75} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function Waitlist() {
  const { state } = useAuthContext();
  const facilityId = state.me?.activeFacility?.id ?? null;

  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<WaitlistStatus | ''>('waiting');
  const [showAdd, setShowAdd] = useState(false);
  const [promoteEntry, setPromoteEntry] = useState<WaitlistEntry | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const data = await listWaitlist({
        facilityId,
        status: statusFilter || undefined,
      });
      setEntries(data);
    } catch (e: unknown) {
      setFetchError((e as Error).message ?? 'Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [facilityId, statusFilter]);

  useEffect(() => {
    void fetchEntries();
  }, [fetchEntries]);

  // Realtime: refresh list when a booking is created (promote from another tab)
  useRealtime((event) => {
    if (event.type === 'BOOKING_CREATED') {
      void fetchEntries();
    }
  });

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleMarkContacted(entry: WaitlistEntry) {
    try {
      await updateWaitlistEntry(entry.id, { status: 'contacted' });
      void fetchEntries();
    } catch (e: unknown) {
      showToast((e as Error).message ?? 'Failed to update entry');
    }
  }

  async function handleCancel(entry: WaitlistEntry) {
    try {
      await updateWaitlistEntry(entry.id, { status: 'cancelled' });
      void fetchEntries();
    } catch (e: unknown) {
      showToast((e as Error).message ?? 'Failed to cancel entry');
    }
  }

  async function handleDelete(entry: WaitlistEntry) {
    try {
      await deleteWaitlistEntry(entry.id);
      void fetchEntries();
    } catch (e: unknown) {
      showToast((e as Error).message ?? 'Failed to delete entry');
    }
  }

  return (
    <div className="space-y-6 p-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-semibold">Waitlist</h1>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as WaitlistStatus | '')}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
          {facilityId && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press"
            >
              <Plus className="size-3.5" strokeWidth={2} />
              Add to waitlist
            </button>
          )}
        </div>
      </div>

      {/* Error state */}
      {fetchError && !loading && (
        <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {fetchError}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {(['sk1', 'sk2', 'sk3'] as const).map((k) => (
            <div key={k} className="h-16 animate-pulse rounded-xl bg-secondary" />
          ))}
        </div>
      ) : !fetchError && entries.length === 0 ? (
        <div
          className={cn(
            'rounded-2xl border border-dashed border-border bg-surface/50 py-16 text-center',
          )}
        >
          <p className="font-heading text-base font-medium text-muted-foreground">
            No one on the waitlist
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {statusFilter
              ? `No entries with status "${statusFilter}".`
              : 'Add someone using the button above.'}
          </p>
          {facilityId && (
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              className="mt-4 flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground press mx-auto"
            >
              <Plus className="size-3.5" strokeWidth={2} />
              Add to waitlist
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <WaitlistRow
              key={entry.id}
              entry={entry}
              onMarkContacted={() => void handleMarkContacted(entry)}
              onPromote={() => setPromoteEntry(entry)}
              onCancel={() => void handleCancel(entry)}
              onDelete={() => void handleDelete(entry)}
            />
          ))}
        </div>
      )}

      {/* Toast */}
      {toast !== null && (
        <output className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background shadow-lg">
          {toast}
        </output>
      )}

      {/* Add modal */}
      {showAdd && facilityId && (
        <AddModal
          facilityId={facilityId}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            void fetchEntries();
          }}
        />
      )}

      {/* Promote modal */}
      {promoteEntry !== null && facilityId && (
        <PromoteModal
          entry={promoteEntry}
          facilityId={facilityId}
          onClose={() => setPromoteEntry(null)}
          onPromoted={() => {
            setPromoteEntry(null);
            showToast('Promoted to booking');
            void fetchEntries();
          }}
        />
      )}
    </div>
  );
}
